import { Injectable } from "@angular/core";
import { TopicTagInterface } from "@core/interfaces/forums/topic.interface";
import { ForumApiService } from "@core/services/api/forum/forum-api.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { BehaviorSubject, Observable, Subject, of } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from "rxjs/operators";

@Injectable({
  providedIn: "root"
})
export class TagGenerationService {
  // Minimum content length to trigger tag generation
  public static readonly MIN_CONTENT_LENGTH = 50;

  // Debounce time for content changes (1000ms)
  private readonly _DEBOUNCE_TIME = 1000;

  // Rate limit for API calls (5 seconds)
  private readonly _RATE_LIMIT = 5000;

  // Cache for generated tags
  private readonly _tagsCache = new Map<string, TopicTagInterface[]>();

  // Flag to track if we're currently fetching tags
  private readonly _isGenerating$ = new BehaviorSubject<boolean>(false);

  // Input for content changes
  private readonly _contentChange$ = new Subject<string>();

  // Track when the last API call was made
  private _lastApiCallTime = 0;

  // Subject to emit automatically generated tags
  private readonly _autoGeneratedTags$ = new Subject<TopicTagInterface[]>();

  // Flag to globally enable/disable auto-generation
  private _autoGenerationEnabled = false;

  constructor(
    private readonly forumApiService: ForumApiService,
    private readonly utilsService: UtilsService
  ) {
    // Set up the content change pipeline with debounce and filtering
    this._contentChange$
      .pipe(
        debounceTime(this._DEBOUNCE_TIME),
        distinctUntilChanged(),
        filter(content => this._shouldGenerateTags(content)),
        switchMap(content => this._generateTags(content))
      )
      .subscribe(tags => {
        if (tags && tags.length > 0) {
          this._autoGeneratedTags$.next(tags);
        }
      });
  }

  /**
   * Get a behavior subject that tracks whether tag generation is in progress
   */
  get isGenerating$(): BehaviorSubject<boolean> {
    return this._isGenerating$;
  }

  /**
   * Get an observable that emits when automatic tags have been generated
   */
  get autoGeneratedTags$(): Observable<TopicTagInterface[]> {
    return this._autoGeneratedTags$.asObservable();
  }

  /**
   * Process content changes and trigger tag generation if appropriate
   * @param content The forum post content
   * @param autoGenerationEnabled Whether auto-generation is enabled
   */
  processContentChange(content: string, autoGenerationEnabled: boolean): void {
    // Update the auto-generation flag
    this._autoGenerationEnabled = autoGenerationEnabled;

    // Only process content changes if auto-generation is enabled
    if (autoGenerationEnabled) {
      this._contentChange$.next(content);
    }
  }

  /**
   * Generate tags immediately (manual trigger)
   * @param content The forum post content
   * @returns Observable with the generated tags or null if generation fails
   */
  generateTagsManually(content: string): Observable<TopicTagInterface[] | null> {
    if (!content || content.length < TagGenerationService.MIN_CONTENT_LENGTH) {
      return of(null);
    }

    return this._generateTags(content);
  }

  /**
   * Check if tag generation should proceed based on content and rate limiting
   */
  private _shouldGenerateTags(content: string): boolean {
    // First check if auto-generation is enabled
    if (!this._autoGenerationEnabled) {
      return false;
    }

    // Check content length
    if (!content || content.length < TagGenerationService.MIN_CONTENT_LENGTH) {
      return false;
    }

    // Check if we already have a cached result
    if (this._tagsCache.has(content)) {
      return false;
    }

    // Apply rate limiting
    const now = Date.now();
    if (now - this._lastApiCallTime < this._RATE_LIMIT) {
      return false;
    }

    return true;
  }

  /**
   * Generate tags from the content
   */
  private _generateTags(content: string): Observable<TopicTagInterface[] | null> {
    // Check cache first
    if (this._tagsCache.has(content)) {
      return of(this._tagsCache.get(content) || null);
    }

    // Update rate limiting timestamp and loading state
    this._lastApiCallTime = Date.now();
    this._isGenerating$.next(true);

    // Make the API call
    return this.forumApiService.generateTopicTags(content).pipe(
      tap(response => {
        // Cache the result
        if (response && response.tags && response.tags.length > 0) {
          // Sort tags by confidence (highest first)
          // Equipment tags without confidence are assumed to have confidence 1.0
          const sortedTags = response.tags.sort((a, b) => {
            const confidenceA = a.confidence !== undefined ? a.confidence : 1.0;
            const confidenceB = b.confidence !== undefined ? b.confidence : 1.0;
            return confidenceB - confidenceA;
          });
          this._tagsCache.set(content, sortedTags);
        }
        this._isGenerating$.next(false);
      }),
      catchError(error => {
        console.error("Error generating tags:", error);
        this._isGenerating$.next(false);
        return of(null);
      }),
      map(response => {
        if (!response || !response.tags) {
          return null;
        }
        // Sort the tags before returning them
        return response.tags.sort((a, b) => {
          const confidenceA = a.confidence !== undefined ? a.confidence : 1.0;
          const confidenceB = b.confidence !== undefined ? b.confidence : 1.0;
          return confidenceB - confidenceA;
        });
      })
    );
  }
}
